import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';

const Icon = ({ d, size = 16, strokeWidth = 2, color = 'currentColor', className = '' }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    check: 'M20 6L9 17l-5-5',
    trash: ['M3 6h18', 'M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6', 'M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2', 'M10 11v6', 'M14 11v6'],
    edit: ['M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7', 'M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z'],
    plus: ['M12 5v14', 'M5 12h14'],
    upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12',
    download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3',
    copy: ['M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z'],
    info: ['M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z', 'M12 16v-4', 'M12 8h.01'],
};

const API = '/api';

// Pre-defined countries removed to use database source

function LimitRow({ label, value }) {
    const isUnlimited = value === -1 || value === undefined || value === null;
    return (
        <div className="mpp-limit-row">
            <span className="mpp-limit-label">{label}</span>
            <span className="mpp-limit-value">{isUnlimited ? 'Unlimited' : value}</span>
        </div>
    );
}

export default function ManagePricePlans({ onEdit, onCreate, user }) {
    const [plans, setPlans] = useState([]);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteId, setDeleteId] = useState(null);
    const [toast, setToast] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [isAllSelected, setIsAllSelected] = useState(true);
    const [showImportScreen, setShowImportScreen] = useState(false);
    const [duplicatePlan, setDuplicatePlan] = useState(null);
    const [duplicateName, setDuplicateName] = useState('');
    const [viewMode, setViewMode] = useState('cards');
    const [currencyDetailsPlan, setCurrencyDetailsPlan] = useState(null);

    const handleExport = () => {
        if (plans.length === 0) { alert('No plans to export.'); return; }
        const exportData = plans.map(p => ({
            'Plan Name': p.name || '',
            'Description': p.description || '',
            'Is Free': p.isFree ? 'Yes' : 'No',
            'Price': p.price || 0,
            'Price Period': p.pricePeriod || '',

            'Drivers Limit': p.driversLimit === -1 ? 'Unlimited' : (p.driversLimit || 0),
            'Routes Limit': p.routesLimit === -1 ? 'Unlimited' : (p.routesLimit || 0),
            'Users Limit': p.usersLimit === -1 ? 'Unlimited' : (p.usersLimit || 0),
            'Fleet Limit': p.fleetLimit === -1 ? 'Unlimited' : (p.fleetLimit || 0),
            'Passenger Limit': p.passengerLimit === -1 ? 'Unlimited' : (p.passengerLimit || 0),
            'Clients Limit': p.clientsLimit === -1 ? 'Unlimited' : (p.clientsLimit || 0),
            'Status': p.isActive ? 'Active' : 'Inactive'
        }));
        const ws = XLSX.utils.json_to_sheet(exportData);
        ws['!cols'] = [
            { wch: 18 }, { wch: 30 }, { wch: 10 },
            { wch: 10 }, { wch: 15 }, { wch: 15 },
            { wch: 15 }, { wch: 15 }, { wch: 15 },
            { wch: 15 }, { wch: 15 }, { wch: 10 }
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Price Plans');
        const timestamp = new Date().toISOString().split('T')[0];
        XLSX.writeFile(wb, `Price_Plans_Export_${timestamp}.xlsx`);
    };

    const uniquePeriods = Array.from(new Set(plans.map(p => p.pricePeriod).filter(Boolean))).sort();

    useEffect(() => {
        if (!selectedPeriod && uniquePeriods.length > 0) {
            setSelectedPeriod(uniquePeriods[0]);
        }
    }, [uniquePeriods, selectedPeriod]);

    const filteredPlans = selectedPeriod
        ? plans.filter(p => p.pricePeriod === selectedPeriod)
        : plans;

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const fetchCountries = useCallback(async () => {
        try {
            const res = await fetch(`${API}/countries`);
            const data = await res.json();
            const activeCountries = (Array.isArray(data) ? data : []).filter(c => c.isActive);
            setCountries(activeCountries);
            if (activeCountries.length > 0 && !selectedCountry) {
                setSelectedCountry(activeCountries[0]);
            }
        } catch (err) {
            console.error("Fetch countries error:", err);
        }
    }, [selectedCountry]);

    const fetchPlans = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API}/price-plans`);
            const data = await res.json();
            setPlans(Array.isArray(data) ? data : []);
        } catch {
            setPlans([]);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchCountries();
        fetchPlans();
    }, [fetchCountries, fetchPlans]);

    const handleToggleActive = async (plan) => {
        try {
            const updated = { ...plan, isActive: !plan.isActive };
            const res = await fetch(`${API}/price-plans/${plan._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'adminName': `${user?.firstName} ${user?.lastName}`,
                    'adminEmail': user?.email
                },
                body: JSON.stringify(updated),
            });
            if (res.ok) {
                setPlans(prev => prev.map(p => p._id === plan._id ? updated : p));
                showToast(updated.isActive ? 'Plan activated' : 'Plan deactivated');
            }
        } catch {
            showToast('Failed to update status', 'error');
        }
    };

    const handleDelete = async (id) => {
        try {
            const res = await fetch(`${API}/price-plans/${id}`, {
                method: 'DELETE',
                headers: {
                    'adminName': `${user?.firstName} ${user?.lastName}`,
                    'adminEmail': user?.email
                }
            });
            if (res.ok) {
                setPlans(prev => prev.filter(p => p._id !== id));
                showToast('Price plan deleted');
            } else {
                showToast('Failed to delete', 'error');
            }
        } catch {
            showToast('Server error', 'error');
        }
        setDeleteId(null);
    };

    const handleDuplicate = async () => {
        if (!duplicateName.trim()) {
            showToast('Please enter a new name', 'error');
            return;
        }
        try {
            const newPlanData = { ...duplicatePlan, name: duplicateName.trim() };
            delete newPlanData._id;
            delete newPlanData.createdAt;
            delete newPlanData.updatedAt;

            const res = await fetch(`${API}/price-plans`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'adminName': `${user?.firstName} ${user?.lastName}`,
                    'adminEmail': user?.email
                },
                body: JSON.stringify(newPlanData),
            });
            if (res.ok) {
                const created = await res.json();
                setPlans(prev => [created.plan, ...prev]);
                showToast('Plan duplicated successfully');
                setDuplicatePlan(null);
            } else {
                showToast('Failed to duplicate plan', 'error');
            }
        } catch {
            showToast('Server error', 'error');
        }
    };

    if (showImportScreen) {
        return (
            <PricePlanImportWizard
                plans={plans}
                onBack={() => setShowImportScreen(false)}
                onDone={() => { setShowImportScreen(false); fetchPlans(); }}
            />
        );
    }

    return (
        <div className="mpp-page">
            {/* Toast */}
            {toast && (
                <div className={`pp-toast ${toast.type}`}>
                    {toast.type === 'error' ? (
                        <Icon d={['M18 6L6 18', 'M6 6l12 12']} size={14} strokeWidth={3} style={{ marginRight: 8 }} />
                    ) : (
                        <Icon d={icons.check} size={14} strokeWidth={3} style={{ marginRight: 8 }} />
                    )}
                    {toast.msg}
                </div>
            )}

            {/* Delete confirm Modal */}
            {deleteId && (
                <div className="mpp-modal-overlay">
                    <div className="mpp-modal-box">
                        <div className="mpp-modal-icon">
                            <Icon d={icons.trash} size={42} color="#dd3232" strokeWidth={1.5} />
                        </div>
                        <h3 className="mpp-modal-title">Delete Price Plan?</h3>
                        <p className="mpp-modal-sub">This action cannot be undone.</p>
                        <div className="mpp-modal-actions">
                            <button className="mpp-btn-cancel" onClick={() => setDeleteId(null)}>Cancel</button>
                            <button className="mpp-btn-confirm" onClick={() => handleDelete(deleteId)}>Yes, Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Duplicate Modal */}
            {duplicatePlan && (
                <div className="mpp-modal-overlay">
                    <div className="mpp-modal-box">
                        <h3 className="mpp-modal-title">Duplicate Plan</h3>
                        <p className="mpp-modal-sub">Enter a new name for the duplicated plan:</p>
                        <input 
                            type="text" 
                            className="mpp-select" 
                            style={{ width: '100%', marginBottom: '20px', padding: '10px' }}
                            value={duplicateName}
                            onChange={(e) => setDuplicateName(e.target.value)}
                            placeholder="New Plan Name"
                        />
                        <div className="mpp-modal-actions">
                            <button className="mpp-btn-cancel" onClick={() => setDuplicatePlan(null)}>Cancel</button>
                            <button className="mpp-btn-confirm" style={{ background: '#3f51b5' }} onClick={handleDuplicate}>Duplicate</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Currency Details Modal */}
            {currencyDetailsPlan && (
                <div className="mpp-modal-overlay">
                    <div className="mpp-modal-box" style={{ maxWidth: '500px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h3 className="mpp-modal-title" style={{ margin: 0 }}>{currencyDetailsPlan.name} - Pricing</h3>
                            <button className="mpp-action-icon" onClick={() => setCurrencyDetailsPlan(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>
                                <Icon d={icons.trash[0]} size={20} color="#64748b" /> {/* Using trash icon just as a placeholder, actually I'll use a close button */}
                                <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#64748b' }}>&times;</span>
                            </button>
                        </div>
                        <div className="mpp-all-currencies" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            {countries.map(c => {
                                const locale = c.locale || 'en-US';
                                const rate = c.exchangeRate || c.rate || 1;
                                const safeLocale = `${locale.split('-u-')[0]}-u-nu-latn`;
                                const displayCurrency = c.currency || '';
                                const specificPriceObj = currencyDetailsPlan.prices?.find(p => p.country === c.name);
                                const finalAmount = specificPriceObj && specificPriceObj.amount != null 
                                    ? specificPriceObj.amount 
                                    : (currencyDetailsPlan.price * rate);
                                return (
                                    <div key={c.name} className="mpp-currency-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e2e8f0' }}>
                                        <span className="mpp-cur-label" style={{ fontWeight: 500 }}>{c.name}</span>
                                        <span className="mpp-cur-val">
                                            {displayCurrency} {finalAmount.toLocaleString(safeLocale, { minimumFractionDigits: 2 })}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mpp-modal-actions" style={{ marginTop: '20px' }}>
                            <button className="mpp-btn-cancel" style={{ width: '100%' }} onClick={() => setCurrencyDetailsPlan(null)}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="superadmin-page-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }} className="header-titles">
                    <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#ffffff' }}>All Plans</h1>
                    <div className="mpp-country-selector">
                        <select
                            className="mpp-select"
                            value={isAllSelected ? 'All' : selectedCountry?.name || ''}
                            onChange={(e) => {
                                if (e.target.value === 'All') {
                                    setIsAllSelected(true);
                                } else {
                                    setIsAllSelected(false);
                                    const country = countries.find(c => c.name === e.target.value);
                                    setSelectedCountry(country);
                                }
                            }}
                        >
                            <option value="All">All Countries</option>
                            {countries.map(c => (
                                <option key={c.name} value={c.name}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="mpp-view-toggle">
                        <button className={`mpp-view-btn ${viewMode === 'table' ? 'active' : ''}`} onClick={() => setViewMode('table')}>Index</button>
                        <button className={`mpp-view-btn ${viewMode === 'cards' ? 'active' : ''}`} onClick={() => setViewMode('cards')}>Cards</button>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <button
                        onClick={() => setShowImportScreen(true)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 16px', borderRadius: 6, border: '1.5px solid #c3dcf5', background: '#eef2ff', color: '#11427D', fontSize: 14, fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s' }}
                    >
                        <Icon d={icons.upload} size={16} strokeWidth={2.5} />
                        Import
                    </button>
                    <button
                        onClick={handleExport}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 16px', borderRadius: 6, border: '1.5px solid #a7f3d0', background: '#ecfdf5', color: '#065f46', fontSize: 14, fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s' }}
                    >
                        <Icon d={icons.download} size={16} strokeWidth={2.5} />
                        Export
                    </button>
                    {(user?.userType === 'admin' || user?.permissions?.includes('Create Price Plan')) && onCreate && (
                        <button className="mpp-btn-create" onClick={onCreate} style={{ margin: 0 }}>
                            <Icon d={icons.plus} size={16} strokeWidth={2.5} style={{ marginRight: 8 }} />
                            Create Plan
                        </button>
                    )}
                </div>
            </div>

            {/* Grid Container */}
            <div className="mpp-outer-container">
                {loading ? (
                    <div className="mpp-loading">Loading plans…</div>
                ) : plans.length === 0 ? (
                    <div className="mpp-empty">No plans found. Create one to get started.</div>
                ) : viewMode === 'table' ? (
                    <div className="mpp-table-container">
                        <table className="mpp-table">
                            <thead>
                                <tr>
                                    <th>Plan Name</th>
                                    <th>Price</th>
                                    <th>Limits</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {plans.map((plan) => (
                                    <tr key={plan._id}>
                                        <td><strong>{plan.name}</strong></td>
                                        <td>
                                            {isAllSelected ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    Multiple Countries
                                                    <button 
                                                        className="mpp-action-icon" 
                                                        style={{ padding: 0, width: '24px', height: '24px', color: '#3b82f6', background: 'transparent', border: 'none', cursor: 'pointer' }}
                                                        onClick={() => setCurrencyDetailsPlan(plan)}
                                                        title="View all prices"
                                                    >
                                                        <Icon d={icons.info} size={16} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div style={{ fontWeight: 500 }}>
                                                    <span style={{ marginRight: 4 }}>{selectedCountry?.currency || ''}</span>
                                                    {(() => {
                                                        if (plan.price == null || !selectedCountry) return '0.00';
                                                        const specificPriceObj = plan.prices?.find(p => p.country === selectedCountry.name);
                                                        const finalAmount = specificPriceObj && specificPriceObj.amount != null 
                                                            ? specificPriceObj.amount 
                                                            : (plan.price * (selectedCountry.exchangeRate || selectedCountry.rate || 1));
                                                        return finalAmount.toLocaleString(
                                                            `${(selectedCountry.locale || 'en-US').split('-u-')[0]}-u-nu-latn`,
                                                            { minimumFractionDigits: 2 }
                                                        );
                                                    })()}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <div style={{ fontSize: '12px', color: '#64748b' }}>
                                                Drivers: {plan.driversLimit === -1 ? 'Unlimited' : plan.driversLimit} | 
                                                Routes: {plan.routesLimit === -1 ? 'Unlimited' : plan.routesLimit} | 
                                                Users: {plan.usersLimit === -1 ? 'Unlimited' : plan.usersLimit} |
                                                Passengers: {plan.passengerLimit === -1 ? 'Unlimited' : plan.passengerLimit} |
                                                Clients: {plan.clientsLimit === -1 ? 'Unlimited' : plan.clientsLimit} | Fleet: {plan.fleetLimit === -1 ? 'Unlimited' : plan.fleetLimit}
                                            </div>
                                        </td>
                                        <td>
                                            <button
                                                className={`mpp-toggle ${plan.isActive ? 'active' : ''}`}
                                                onClick={() => handleToggleActive(plan)}
                                                style={{ margin: 0 }}
                                            >
                                                <div className="mpp-toggle-thumb" />
                                            </button>
                                        </td>
                                        <td>
                                            <div className="mpp-header-actions" style={{ justifyContent: 'flex-start' }}>
                                                {(user?.userType === 'admin' || user?.permissions?.includes('Edit Price Plan')) && (
                                                    <button className="mpp-action-icon" onClick={async () => {
                                                        try {
                                                            const res = await fetch(`/api/price-plans/${plan._id}/check-subscriptions`);
                                                            const data = await res.json();
                                                            if (data.hasActive) {
                                                                showToast('Cannot edit this plan because it has active subscriptions.', 'error');
                                                            } else {
                                                                onEdit(plan);
                                                            }
                                                        } catch (e) {
                                                            showToast('Error checking plan status', 'error');
                                                        }
                                                    }}>
                                                        <Icon d={icons.edit} size={16} />
                                                    </button>
                                                )}
                                                {(user?.userType === 'admin' || user?.permissions?.includes('Create Price Plan')) && (
                                                    <button className="mpp-action-icon" onClick={() => {
                                                        setDuplicatePlan(plan);
                                                        setDuplicateName(`${plan.name} (Copy)`);
                                                    }}>
                                                        <Icon d={icons.copy} size={16} />
                                                    </button>
                                                )}
                                                {(user?.userType === 'admin' || user?.permissions?.includes('Delete Price Plan')) && (
                                                    <button className="mpp-action-icon" onClick={() => setDeleteId(plan._id)}>
                                                        <Icon d={icons.trash} size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="mpp-grid">
                        {plans.map((plan) => (
                            <div key={plan._id} className="mpp-card">
                                {/* Card Header (Light Blue) */}
                                <div className="mpp-card-header">
                                    <div className="mpp-card-header-top">
                                        <button
                                            className={`mpp-toggle ${plan.isActive ? 'active' : ''}`}
                                            onClick={() => handleToggleActive(plan)}
                                        >
                                            <div className="mpp-toggle-thumb" />
                                        </button>
                                        <div className="mpp-header-actions">
                                            {(user?.userType === 'admin' || user?.permissions?.includes('Edit Price Plan')) && (
                                                <button className="mpp-action-icon" onClick={async () => {
                                                    try {
                                                        const res = await fetch(`/api/price-plans/${plan._id}/check-subscriptions`);
                                                        const data = await res.json();
                                                        if (data.hasActive) {
                                                            showToast('Cannot edit this plan because it has active subscriptions.', 'error');
                                                        } else {
                                                            onEdit(plan);
                                                        }
                                                    } catch (e) {
                                                        showToast('Error checking plan status', 'error');
                                                    }
                                                }}>
                                                    <Icon d={icons.edit} size={16} />
                                                </button>
                                            )}
                                            {(user?.userType === 'admin' || user?.permissions?.includes('Create Price Plan')) && (
                                                <button className="mpp-action-icon" onClick={() => {
                                                    setDuplicatePlan(plan);
                                                    setDuplicateName(`${plan.name} (Copy)`);
                                                }}>
                                                    <Icon d={icons.copy} size={16} />
                                                </button>
                                            )}
                                            {(user?.userType === 'admin' || user?.permissions?.includes('Delete Price Plan')) && (
                                                <button className="mpp-action-icon" onClick={() => setDeleteId(plan._id)}>
                                                    <Icon d={icons.trash} size={16} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <h2 className="mpp-plan-name">{plan.name}</h2>
                                    <div className="mpp-underline" />
                                </div>

                                {/* Card Body (White) */}
                                <div className="mpp-card-body">
                                    {isAllSelected ? (
                                        <div className="mpp-all-currencies">
                                            {countries.map(c => {
                                                const locale = c.locale || 'en-US';
                                                const rate = c.exchangeRate || c.rate || 1;
                                                // Always force Latin (Western) numerals regardless of region locale
                                                const safeLocale = `${locale.split('-u-')[0]}-u-nu-latn`;
                                                const displayCurrency = c.currency || '';
                                                const specificPriceObj = plan.prices?.find(p => p.country === c.name);
                                                const finalAmount = specificPriceObj && specificPriceObj.amount != null 
                                                    ? specificPriceObj.amount 
                                                    : (plan.price * rate);
                                                return (
                                                    <div key={c.name} className="mpp-currency-row">
                                                        <span className="mpp-cur-label">{c.name}</span>
                                                        <span className="mpp-cur-val">
                                                            {displayCurrency} {finalAmount.toLocaleString(safeLocale, { minimumFractionDigits: 2 })}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="mpp-price" style={{ flexDirection: 'column', gap: '4px' }}>
                                            <div>
                                                <span className="mpp-currency">{selectedCountry?.currency || ''}</span>
                                                {(() => {
                                                    if (plan.price == null || !selectedCountry) return '0.00';
                                                    const specificPriceObj = plan.prices?.find(p => p.country === selectedCountry.name);
                                                    const finalAmount = specificPriceObj && specificPriceObj.amount != null 
                                                        ? specificPriceObj.amount 
                                                        : (plan.price * (selectedCountry.exchangeRate || selectedCountry.rate || 1));
                                                    return finalAmount.toLocaleString(
                                                        `${(selectedCountry.locale || 'en-US').split('-u-')[0]}-u-nu-latn`,
                                                        { minimumFractionDigits: 2 }
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    )}

                                    <div className="mpp-limits">

                                        <LimitRow label="Drivers Limit" value={plan.driversLimit} />
                                        <LimitRow label="Routes Limit" value={plan.routesLimit} />
                                        <LimitRow label="Users Limit" value={plan.usersLimit} />
                                        <LimitRow label="Fleet Limit" value={plan.fleetLimit} />
                                        <LimitRow label="Passenger Limit" value={plan.passengerLimit} />
                                        <LimitRow label="Clients Limit" value={plan.clientsLimit} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <style>{`
                .mpp-page {
                    padding: 30px;
                    background: #f8fafc;
                    min-height: 100vh;
                }
                .mpp-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 24px;
                }
                .mpp-title {
                    font-size: 22px;
                    font-weight: 600;
                    color: #334155;
                    margin: 0;
                }
                
                .mpp-view-toggle {
                    display: flex;
                    background: #e2e8f0;
                    border-radius: 8px;
                    padding: 4px;
                    gap: 4px;
                }
                .mpp-view-btn {
                    padding: 6px 16px;
                    border-radius: 6px;
                    border: none;
                    background: transparent;
                    color: #475569;
                    font-weight: 500;
                    font-size: 14px;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .mpp-view-btn.active {
                    background: white;
                    color: #3f51b5;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                }
                .mpp-view-btn:hover:not(.active) {
                    color: #1e293b;
                }

                .mpp-table-container {
                    width: 100%;
                    overflow-x: auto;
                }
                .mpp-table {
                    width: 100%;
                    border-collapse: collapse;
                    text-align: left;
                }
                .mpp-table th {
                    background: #f8fafc;
                    padding: 12px 16px;
                    font-size: 14px;
                    color: #475569;
                    font-weight: 600;
                    border-bottom: 2px solid #e2e8f0;
                }
                .mpp-table td {
                    padding: 16px;
                    border-bottom: 1px solid #e2e8f0;
                    color: #334155;
                    font-size: 14px;
                    vertical-align: middle;
                }
                .mpp-table tr:hover {
                    background: #f1f5f9;
                }

                .mpp-btn-create {
                    display: flex;
                    align-items: center;
                    background: #3f51b5;
                    color: white;
                    border: none;
                    padding: 8px 16px;
                    border-radius: 6px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s;
                    box-shadow: 0 4px 6px -1px rgba(63, 81, 181, 0.2);
                }
                .mpp-btn-create:hover { background: #303f9f; transform: translateY(-1px); }

                .mpp-select {
                    padding: 6px 12px;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    background: white;
                    color: #475569;
                    font-size: 14px;
                    outline: none;
                    cursor: pointer;
                    transition: border-color 0.2s;
                }
                .mpp-select:hover { border-color: #3f51b5; }

                .mpp-outer-container {
                    background: white;
                    padding: 30px;
                    border-radius: 12px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                    border: 1px solid #e2e8f0;
                }
                .mpp-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 24px;
                }
                @media (max-width: 1200px) { .mpp-grid { grid-template-columns: repeat(3, 1fr); } }
                @media (max-width: 900px) { .mpp-grid { grid-template-columns: repeat(2, 1fr); } }
                @media (max-width: 600px) { .mpp-grid { grid-template-columns: 1fr; } }

                .mpp-card {
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 10px;
                    overflow: hidden;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
                    display: flex;
                    flex-direction: column;
                }

                .mpp-card-header {
                    background: #eef2ff;
                    padding: 24px 20px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    border-bottom: 1px solid #e2e8f0;
                }
                .mpp-card-header-top {
                    width: 100%;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 30px;
                }
                .mpp-header-actions { display: flex; gap: 12px; }
                .mpp-action-icon {
                    background: none;
                    border: none;
                    color: #94a3b8;
                    cursor: pointer;
                    padding: 0;
                    display: flex;
                    align-items: center;
                    transition: color 0.2s;
                }
                .mpp-action-icon:hover { color: #64748b; }

                /* Toggle Switch */
                .mpp-toggle {
                    width: 44px;
                    height: 24px;
                    background: #cbd5e1;
                    border-radius: 12px;
                    border: none;
                    position: relative;
                    cursor: pointer;
                    transition: background 0.2s;
                }
                .mpp-toggle.active { background: #3f51b5; }
                .mpp-toggle-thumb {
                    width: 18px;
                    height: 18px;
                    background: white;
                    border-radius: 50%;
                    position: absolute;
                    top: 3px;
                    left: 3px;
                    transition: transform 0.2s;
                }
                .mpp-toggle.active .mpp-toggle-thumb { transform: translateX(20px); }

                .mpp-plan-name {
                    font-size: 28px;
                    font-weight: 600;
                    color: #475569;
                    margin: 0;
                    letter-spacing: -0.5px;
                }
                .mpp-underline {
                    width: 60%;
                    height: 1px;
                    background: #cbd5e1;
                    margin-top: 24px;
                }

                .mpp-card-body {
                    padding: 24px;
                    background: white;
                    flex-grow: 1;
                }
                .mpp-price {
                    font-size: 36px;
                    font-weight: 600;
                    color: #475569;
                    text-align: center;
                    margin-bottom: 30px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    letter-spacing: -1px;
                }
                .mpp-currency { font-size: 24px; margin-right: 2px; }

                .mpp-limits {
                    display: flex;
                    flex-direction: column;
                    gap: 18px;
                }
                .mpp-limit-row {
                    display: flex;
                    justify-content: space-between;
                    font-size: 13.5px;
                    color: #64748b;
                }
                .mpp-limit-value {
                    font-weight: 500;
                    color: #475569;
                }

                .mpp-all-currencies {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                    margin-bottom: 24px;
                    border-bottom: 1px solid #f1f5f9;
                    padding-bottom: 16px;
                    max-height: 200px;
                    overflow-y: auto;
                    padding-right: 4px;
                }
                .mpp-all-currencies::-webkit-scrollbar { width: 4px; }
                .mpp-all-currencies::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }

                .mpp-currency-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    font-size: 13px;
                }
                .mpp-cur-label { color: #94a3b8; font-weight: 500; }
                .mpp-cur-val { color: #475569; font-weight: 600; }

                .mpp-modal-overlay {
                    position: fixed;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(0,0,0,0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                }
                .mpp-modal-box {
                    background: white;
                    padding: 32px;
                    border-radius: 12px;
                    max-width: 400px;
                    width: 90%;
                    text-align: center;
                }

                /* --- Dark Mode Overrides --- */
                .theme-dark .mpp-page {
                    background: #0f0f0f;
                }
                .theme-dark .mpp-title {
                    color: #e2e8f0;
                }
                .theme-dark .mpp-select {
                    background: #1e1e1e;
                    border-color: #2a2a2a;
                    color: #aaa;
                }
                .theme-dark .mpp-outer-container {
                    background: #161616;
                    border-color: #222;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.3);
                }
                .theme-dark .mpp-card {
                    background: #1a1a1a;
                    border-color: #242424;
                }
                .theme-dark .mpp-card-header {
                    background: #1e1e1e;
                    border-bottom-color: #242424;
                }
                .theme-dark .mpp-action-icon {
                    color: #64748b;
                }
                .theme-dark .mpp-action-icon:hover {
                    color: #94a3b8;
                }
                .theme-dark .mpp-toggle {
                    background: #2a2a2a;
                }
                .theme-dark .mpp-plan-name {
                    color: #fff;
                }
                .theme-dark .mpp-underline {
                    background: #2a2a2a;
                }
                .theme-dark .mpp-card-body {
                    background: #1a1a1a;
                }
                .theme-dark .mpp-price {
                    color: #fff;
                }
                .theme-dark .mpp-limit-row {
                    color: #888;
                }
                .theme-dark .mpp-limit-value {
                    color: #ccc;
                }
                .theme-dark .mpp-all-currencies {
                    border-color: #242424;
                }
                .theme-dark .mpp-cur-label { color: #64748b; }
                .theme-dark .mpp-cur-val { color: #fff; }
                .theme-dark .mpp-all-currencies::-webkit-scrollbar-thumb { background: #2a2a2a; }
                .theme-dark .mpp-modal-box {
                    background: #1a1a1a;
                    border: 1px solid #242424;
                }
                .theme-dark .mpp-modal-title {
                    color: #fff;
                }
                .theme-dark .mpp-modal-sub {
                    color: #888;
                }
                .theme-dark .mpp-btn-cancel {
                    background: #2a2a2a;
                    color: #ccc;
                }
                .theme-dark .mpp-loading,
                .theme-dark .mpp-empty {
                    color: #555;
                }
            `}</style>
        </div >
    );
}

function PricePlanImportWizard({ plans, onBack, onDone }) {
    const [file, setFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [parsedData, setParsedData] = useState([]);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);

    // Map of existing plans (lowercase name -> true)
    const existingNames = new Set(plans.map(p => (p.name || '').trim().toLowerCase()));

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            processFile(e.dataTransfer.files[0]);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            processFile(e.target.files[0]);
        }
    };

    const processFile = (selectedFile) => {
        setFile(selectedFile);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const bstr = e.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);

                // Standardize headers
                const standardized = data.map((row) => {
                    const mapped = {};
                    Object.keys(row).forEach(key => {
                        const cleanKey = key.trim();
                        mapped[cleanKey] = row[key];
                    });
                    return mapped;
                });

                setParsedData(standardized);
            } catch (err) {
                console.error(err);
                alert("Failed to parse Excel file. Please ensure it is a valid .xlsx or .xls file.");
            }
        };
        reader.readAsBinaryString(selectedFile);
    };

    const downloadTemplate = () => {
        const templateData = [
            {
                'Plan Name': 'Starter',
                'Description': 'Basic plan for small teams',
                'Is Free': 'No',
                'Price': 200,
                'Price Period': 'monthly',
                'Vehicles Limit': 10,
                'Drivers Limit': 9,
                'Routes Limit': 8,
                'Users Limit': 7,
                'Passenger Limit': 50,
                'Clients Limit': 5,
                'Status': 'Active'
            },
            {
                'Plan Name': 'Enterprise',
                'Description': 'Advanced plan for large operations',
                'Is Free': 'No',
                'Price': 3000,
                'Price Period': 'yearly',
                'Vehicles Limit': 20,
                'Drivers Limit': 9,
                'Routes Limit': 9,
                'Users Limit': 8,
                'Status': 'Active'
            }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        ws['!cols'] = [
            { wch: 18 }, { wch: 30 }, { wch: 10 },
            { wch: 10 }, { wch: 15 }, { wch: 15 },
            { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 10 }
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Template');
        XLSX.writeFile(wb, 'Price_Plans_Import_Template.xlsx');
    };

    const handleImport = async () => {
        if (parsedData.length === 0) return;
        setImporting(true);
        try {
            const res = await fetch(`/api/price-plans/import`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ plans: parsedData })
            });
            const data = await res.json();
            if (res.ok) {
                setResult(data);
            } else {
                alert(data.message || "Import failed. Please try again.");
            }
        } catch (err) {
            console.error(err);
            alert("Import failed. Please try again.");
        } finally {
            setImporting(false);
        }
    };

    const matchedCount = parsedData.filter(d => {
        const name = (d.name || d['Plan Name'] || d['Name'] || '').trim().toLowerCase();
        return name && existingNames.has(name);
    }).length;

    const newCount = parsedData.filter(d => {
        const name = (d.name || d['Plan Name'] || d['Name'] || '').trim().toLowerCase();
        return name && !existingNames.has(name);
    }).length;

    if (result) {
        return (
            <div className="mpp-page" style={{ fontFamily: "'Inter', sans-serif", padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
                <div style={{ background: 'white', borderRadius: '16px', padding: '40px', textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '80px', height: '80px', borderRadius: '50%', background: '#d1fae5', color: '#10b981', marginBottom: '24px' }}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                    </div>
                    <h2 style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b', marginBottom: '12px' }}>Import Completed!</h2>
                    <p style={{ fontSize: '15px', color: '#64748b', maxWidth: '600px', margin: '0 auto 30px' }}>
                        The price plan data has been processed successfully. Existing plans were updated and new ones were created.
                    </p>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', maxWidth: '700px', margin: '0 auto 30px' }}>
                        <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#065f46' }}>{result.updatedCount || 0}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#047857', marginTop: '4px' }}>Plans Updated</div>
                        </div>
                        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#1e40af' }}>{result.createdCount || 0}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#1d4ed8', marginTop: '4px' }}>New Plans Created</div>
                        </div>
                        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#92400e' }}>{(result.errors?.length) || 0}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#b45309', marginTop: '4px' }}>Skipped / Warnings</div>
                        </div>
                    </div>

                    {result.errors && result.errors.length > 0 && (
                        <div style={{ maxWidth: '750px', margin: '0 auto 30px', textAlign: 'left' }}>
                            <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#475569', marginBottom: '10px' }}>Details / Warnings:</h4>
                            <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', background: '#f8fafc' }}>
                                {result.errors.map((err, idx) => (
                                    <div key={idx} style={{ fontSize: '13px', color: '#64748b', padding: '6px 0', borderBottom: idx < result.errors.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                                        ⚠️ Row {err.row}: {err.message}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <button
                        onClick={onDone}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 30px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg,#16539C,#00B2A9)', color: 'white', fontSize: '15px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.3)', transition: 'all 0.15s' }}
                    >
                        Return to Price Plans
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="mpp-page" style={{ fontFamily: "'Inter', sans-serif", padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '30px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button
                        onClick={onBack}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '10px', border: '1px solid #e2e8f0', background: 'white', color: '#64748b', cursor: 'pointer', transition: 'all 0.15s' }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="19" y1="12" x2="5" y2="12" />
                            <polyline points="12 19 5 12 12 5" />
                        </svg>
                    </button>
                    <div>
                        <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: 0 }}>Import & Update Price Plans</h1>
                        <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0' }}>Bulk import new price plans or update existing ones via Excel upload.</p>
                    </div>
                </div>
            </div>

            {parsedData.length === 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: '400px 1fr', gap: '30px' }}>
                    {/* Instructions Left Column */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                ℹ️ How it works
                            </h3>
                            <ul style={{ paddingLeft: '20px', margin: 0, display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '13.5px', color: '#475569', lineHeight: '1.5' }}>
                                <li>The file must be an Excel sheet (<code>.xlsx</code> or <code>.xls</code>).</li>
                                <li>It matches uploaded rows to <strong>EXISTING</strong> plans in the database by their <strong>Plan Name</strong> (case-insensitive) to update them.</li>
                                <li style={{ fontWeight: '600', color: '#16539C' }}>Unmatched plan names will automatically be created as brand new price plans!</li>
                                <li>Limits, prices, descriptions, and statuses will be mapped and set.</li>
                            </ul>
                        </div>

                        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                            <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' }}>Supported Fields</h3>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {['Plan Name *', 'Description', 'Is Free', 'Price', 'Price Period', 'Vehicles Limit', 'Drivers Limit', 'Routes Limit', 'Users Limit', 'Status'].map((f, i) => (
                                    <span key={i} style={{ fontSize: '11px', fontWeight: '600', color: '#11427D', background: '#eef2ff', border: '1px solid #c3dcf5', padding: '4px 8px', borderRadius: '6px' }}>
                                        {f}
                                    </span>
                                ))}
                            </div>
                            <p style={{ fontSize: '11.5px', color: '#64748b', marginTop: '10px', margin: '10px 0 0' }}>* <strong>Plan Name</strong> is required. All limits default to -1 (unlimited) if left empty.</p>
                        </div>

                        <div style={{ background: '#f8fafc', borderRadius: '16px', border: '1px dashed #cbd5e1', padding: '20px', textAlign: 'center' }}>
                            <p style={{ fontSize: '13px', color: '#475569', margin: '0 0 12px' }}>Need a starting point? Download our pre-configured format template.</p>
                            <button
                                onClick={downloadTemplate}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', border: '1.5px solid #cbd5e1', background: 'white', color: '#334155', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s' }}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                                Download Sample Template
                            </button>
                        </div>
                    </div>

                    {/* Upload Box Right Column */}
                    <div
                        onDragEnter={handleDrag}
                        onDragOver={handleDrag}
                        onDragLeave={handleDrag}
                        onDrop={handleDrop}
                        style={{
                            background: 'white',
                            borderRadius: '16px',
                            border: `2px dashed ${dragActive ? '#16539C' : '#cbd5e1'}`,
                            backgroundColor: dragActive ? '#f0f7ff' : 'white',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '60px 40px',
                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                            transition: 'all 0.15s',
                            cursor: 'pointer'
                        }}
                        onClick={() => document.getElementById('file-upload-input').click()}
                    >
                        <input
                            id="file-upload-input"
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />
                        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', borderRadius: '16px', background: '#eef2ff', color: '#16539C', marginBottom: '20px' }}>
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                        </div>
                        <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: '0 0 8px' }}>Drag & Drop Excel File</h3>
                        <p style={{ fontSize: '13.5px', color: '#64748b', margin: '0 0 16px' }}>or click to browse your local files</p>
                        <span style={{ fontSize: '12px', color: '#94a3b8', background: '#f8fafc', padding: '6px 12px', borderRadius: '6px', border: '1px solid #f1f5f9' }}>
                            Supports .xlsx, .xls formats
                        </span>
                    </div>
                </div>
            ) : (
                <div style={{ background: 'white', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '20px', borderBottom: '1px solid #f1f5f9', marginBottom: '20px' }}>
                        <div>
                            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', margin: 0 }}>Review Price Plans</h3>
                            <p style={{ fontSize: '12.5px', color: '#64748b', margin: '4px 0 0' }}>
                                Found <strong>{parsedData.length} records</strong>. We will match existing plans by name to update them, and create any new ones.
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                onClick={() => { setFile(null); setParsedData([]); }}
                                style={{ padding: '8px 16px', borderRadius: '8px', border: '1.5px solid #cbd5e1', background: 'white', color: '#475569', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                            >
                                Reset
                            </button>
                            <button
                                onClick={handleImport}
                                disabled={importing}
                                style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#16539C,#00B2A9)', color: 'white', fontSize: '13px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.2)', opacity: importing ? 0.7 : 1 }}
                            >
                                {importing ? 'Processing...' : `Proceed Import (${matchedCount + newCount})`}
                            </button>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '13px', color: '#166534', fontWeight: '600' }}>To Update (Match)</span>
                            <span style={{ fontSize: '18px', color: '#15803d', fontWeight: '800' }}>{matchedCount}</span>
                        </div>
                        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '13px', color: '#1e40af', fontWeight: '600' }}>To Create (New)</span>
                            <span style={{ fontSize: '18px', color: '#1d4ed8', fontWeight: '800' }}>{newCount}</span>
                        </div>
                    </div>

                    <div style={{ overflowX: 'auto', maxHeight: '350px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #cbd5e1' }}>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Plan Name</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Description</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Price</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Period</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Vehicles</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Operators</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Routes</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Users</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Passengers</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Clients</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Import Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {parsedData.map((row, idx) => {
                                    const name = (row.name || row['Plan Name'] || row['Name'] || '').trim();
                                    const desc = row.description || row['Description'] || '';
                                    const price = row.price || row['Price'] || 0;
                                    const period = row.pricePeriod || row['Price Period'] || row['Period'] || '';
                                    const vehicles = row.fleetLimit || row['Fleet Limit'] || row['Fleet'] || row.vehiclesLimit || row['Vehicles Limit'] || row['Vehicles'] || -1;
                                    const operators = row.driversLimit || row['Drivers Limit'] || row['Drivers'] || -1;
                                    const routes = row.routesLimit || row['Routes Limit'] || row['Routes'] || -1;
                                    const users = row.usersLimit || row['Users Limit'] || row['Users'] || -1;
                                    const passengers = row.passengerLimit || row['Passenger Limit'] || row['Passengers'] || -1;
                                    const clients = row.clientsLimit || row['Clients Limit'] || row['Clients'] || -1;
                                    
                                    const isMatch = name && existingNames.has(name.toLowerCase());
                                    const actionText = isMatch ? 'Update Existing' : 'Create New';
                                    const actionBg = isMatch ? '#f0fdf4' : '#eff6ff';
                                    const actionColor = isMatch ? '#166534' : '#1e40af';

                                    return (
                                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '12px 16px', fontWeight: '600' }}>{name || <span style={{ color: '#ef4444' }}>Missing Name</span>}</td>
                                            <td style={{ padding: '12px 16px', color: '#64748b', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{desc}</td>
                                            <td style={{ padding: '12px 16px' }}>{price}</td>
                                            <td style={{ padding: '12px 16px' }}>{period}</td>
                                            <td style={{ padding: '12px 16px' }}>{vehicles}</td>
                                            <td style={{ padding: '12px 16px' }}>{operators}</td>
                                            <td style={{ padding: '12px 16px' }}>{routes}</td>
                                            <td style={{ padding: '12px 16px' }}>{users}</td>
                                            <td style={{ padding: '12px 16px' }}>{passengers}</td>
                                            <td style={{ padding: '12px 16px' }}>{clients}</td>
                                            <td style={{ padding: '12px 16px' }}>
                                                <span style={{ fontSize: '11px', fontWeight: '700', padding: '4px 8px', borderRadius: '4px', backgroundColor: actionBg, color: actionColor }}>
                                                    {actionText}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
